import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useWeb3 } from '../contexts/Web3Context';
import ApiService from '../services/api';
import PassportCard from './PassportCard';
import { FaWallet, FaTrophy, FaUsers, FaCode } from "react-icons/fa";
import { RiPassportLine } from "react-icons/ri";
import { FaTwitter, FaGithub, FaDiscord, FaCheck } from "react-icons/fa";
import MobileWalletGuide from './MobileWalletGuide';
import { notify } from '../utils/notifications';
import { useReferralFromURL } from '../hooks/useReferralFromURL';
import { validateReferralCode } from '../utils/validateReferralCode';

const ErrorDisplay = ({ error, errorCategory, suggestions, onRetry, onRefresh }) => {
  const getErrorIcon = () => {
    switch (errorCategory) {
      case 'account_claimed':
      case 'duplicate_passport':
        return '⚠️';
      case 'unsupported_platform':
      case 'unsupported_category':
        return 'ℹ️';
      case 'unauthorized':
        return '🔒';
      default:
        return '❌';
    }
  };

  const getErrorColor = () => {
    switch (errorCategory) {
      case 'account_claimed':
      case 'duplicate_passport':
        return 'warning';
      case 'unsupported_platform':
      case 'unsupported_category':
        return 'info';
      default:
        return 'error';
    }
  };

  return (
    <div className={`alert alert-${getErrorColor()}`} style={{
      padding: '1.5rem',
      marginBottom: '1.5rem',
      borderRadius: '12px',
      backgroundColor: getErrorColor() === 'warning' 
        ? 'rgba(255, 193, 7, 0.1)' 
        : getErrorColor() === 'info'
          ? 'rgba(23, 162, 184, 0.1)'
          : 'rgba(220, 53, 69, 0.1)',
      border: `1px solid ${getErrorColor() === 'warning' 
        ? 'rgba(255, 193, 7, 0.3)' 
        : getErrorColor() === 'info'
          ? 'rgba(23, 162, 184, 0.3)'
          : 'rgba(220, 53, 69, 0.3)'}`,
      color: getErrorColor() === 'warning' 
        ? 'var(--color-warning)' 
        : getErrorColor() === 'info'
          ? 'var(--color-info)'
          : 'var(--color-danger)'
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
        <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>
          {getErrorIcon()}
        </span>
        <div style={{ flex: 1 }}>
          <h4 style={{ 
            margin: '0 0 0.5rem 0', 
            fontSize: '1.1rem', 
            fontWeight: '600' 
          }}>
            {errorCategory === 'account_claimed' && 'Account Already Claimed'}
            {errorCategory === 'duplicate_passport' && 'Passport Already Exists'}
            {errorCategory === 'unsupported_platform' && 'Platform Not Supported'}
            {errorCategory === 'unsupported_category' && 'Category Not Supported'}
            {errorCategory === 'unauthorized' && 'Not Authorized'}
            {!errorCategory && 'Verification Failed'}
          </h4>
          <p style={{ margin: '0 0 1rem 0', lineHeight: '1.5' }}>
            {error}
          </p>
          
          {suggestions && suggestions.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ 
                margin: '0 0 0.5rem 0', 
                fontWeight: '600', 
                fontSize: '0.9rem' 
              }}>
                What you can do:
              </p>
              <ul style={{ 
                margin: '0', 
                paddingLeft: '1.2rem', 
                fontSize: '0.9rem' 
              }}>
                {suggestions.map((suggestion, index) => (
                  <li key={index} style={{ marginBottom: '0.25rem' }}>
                    {suggestion}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {errorCategory === 'duplicate_passport' && onRefresh && (
              <button 
                onClick={onRefresh}
                className="btn btn-sm btn-outline"
                style={{ 
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  borderRadius: '6px'
                }}
              >
                Refresh Page
              </button>
            )}
            
            {(errorCategory === 'network' || !errorCategory) && onRetry && (
              <button 
                onClick={onRetry}
                className="btn btn-sm btn-outline"
                style={{ 
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  borderRadius: '6px'
                }}
              >
                Try Again
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

function Dashboard() {
const { 
  account, 
  isConnected, 
  connectWallet, 
  isConnecting, 
  contract, 
  createPassportWithSignature,
  // Solana wallet methods
  getAvailableSolanaWallets,
  connectSolanaWallet,
  connectSpecificSolanaWallet,
  disconnectSolanaWallet,
  verifySolanaWithSignature,
  solanaWalletType
} = useWeb3();

  // Use referral hook instead of local state
  const {
    referralCode,
    referralSource,
    isFromURL,
    updateReferralCode,
    clearReferralCode,
    generateReferralURL
  } = useReferralFromURL();
  
  const [passport, setPassport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creatingPassport, setCreatingPassport] = useState(false);
  const [verifiedPlatforms, setVerifiedPlatforms] = useState({});
  const [pendingVerifications, setPendingVerifications] = useState({});
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const [revokingPlatform, setRevokingPlatform] = useState(null);
  const navigate = useNavigate();
  const [supportedCategories, setSupportedCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [showSolanaWalletSelector, setShowSolanaWalletSelector] = useState(false);
  const [availableSolanaWallets, setAvailableSolanaWallets] = useState([]);
  const [validatingReferral, setValidatingReferral] = useState(false);
  
  // Add Solana-specific state
  const [solanaVerifying, setSolanaVerifying] = useState(false);
  const [solanaError, setSolanaError] = useState(null);

  // Add Solana verification handler
const handleSolanaVerification = async (selectedWalletType = null) => {
  if (!isConnected || !account) {
    setError('Please connect your Ethereum wallet first');
    return;
  }
  
  setSolanaVerifying(true);
  setSolanaError(null);
  
  try {
    // Get available wallets
    const wallets = getAvailableSolanaWallets();
    
    if (wallets.length === 0) {
      throw new Error('No Solana wallets found. Please install Phantom or Solflare wallet.');
    }
    
    // If no specific wallet selected and multiple available, show selector
    if (!selectedWalletType && wallets.length > 1) {
      setAvailableSolanaWallets(wallets);
      setShowSolanaWalletSelector(true);
      setSolanaVerifying(false);
      return;
    }
    
    // Connect to wallet
    const connection = selectedWalletType 
      ? await connectSpecificSolanaWallet(selectedWalletType)
      : await connectSolanaWallet();
    
    // Show which wallet was connected
    if (connection.walletName) {
      notify.info(`Connected to ${connection.walletName} wallet`);
    }
    
    // Small delay to ensure wallet is properly connected
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Verify Solana address
    const result = await verifySolanaWithSignature();
    
    if (result.success) {
      notify.success(`🎉 Solana address verified!`);
      
      // Refresh passport data
      const { passport: updatedPassport } = await ApiService.getPassportByAddress(account);
      setPassport(updatedPassport);
      
      // Update verified platforms
      if (updatedPassport && updatedPassport.verifications) {
        const verified = {};
        Object.keys(updatedPassport.verifications).forEach(platform => {
          verified[platform] = true;
        });
        setVerifiedPlatforms(verified);
      }
      
      // Disconnect Solana wallet after successful verification
      await disconnectSolanaWallet();
    } else {
      // Handle different error types
      if (result.errorCategory === 'account_claimed') {
        notify.errorWithSuggestions(
          'This Solana address has already been verified by another passport',
          'account_claimed',
          [
            'Each Solana address can only be linked to one passport',
            'Try using a different Solana address or wallet',
            'Contact support if you believe this is an error'
          ]
        );
      } else if (result.errorCategory === 'duplicate_verification') {
        notify.errorWithSuggestions(
          'Your passport already has a Solana address verified',
          'duplicate_verification',
          [
            'You can only link one Solana address per passport',
            'You can revoke the existing verification to add a new one'
          ]
        );
      } else {
        notify.error(result.error || 'Failed to verify Solana address');
      }
      
      setSolanaError(result.error);
    }
  } catch (error) {
    console.error('Solana verification error:', error);
    
    let errorMessage = error.message;
    
    if (errorMessage.includes('No Solana wallets found')) {
      setSolanaError('No Solana wallets found. Please install Phantom or Solflare wallet.');
      notify.errorWithSuggestions(
        'No Solana wallets found',
        'wallet_not_found',
        [
          'Install Phantom wallet from phantom.app',
          'Install Solflare wallet from solflare.com',
          'Refresh the page after installation',
          'Make sure the wallet extension is enabled in your browser'
        ]
      );
    } else if (errorMessage.includes('not found')) {
      setSolanaError(errorMessage);
      notify.errorWithSuggestions(
        errorMessage,
        'wallet_not_found',
        [
          'Make sure the wallet is properly installed',
          'Try refreshing the page',
          'Check if the wallet extension is enabled'
        ]
      );
    } else if (errorMessage.includes('User rejected')) {
      setSolanaError('Connection cancelled by user');
      notify.warning('Solana wallet connection was cancelled');
    } else {
      setSolanaError(errorMessage);
      //notify.error(errorMessage || 'Failed to verify Solana address');
    }
  } finally {
    setSolanaVerifying(false);
    setShowSolanaWalletSelector(false);
  }
};

const SolanaWalletSelector = () => {
  if (!showSolanaWalletSelector) return null;
  
  return (
    <div className="wallet-selector-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div className="wallet-selector-modal" style={{
        background: 'linear-gradient(145deg, rgba(30, 30, 30, 0.95), rgba(20, 20, 20, 0.98))',
        borderRadius: '20px',
        padding: '2rem',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
        backdropFilter: 'blur(10px)',
        maxWidth: '400px',
        width: '90%'
      }}>
        <h3 style={{
          color: 'white',
          marginBottom: '1.5rem',
          textAlign: 'center',
          fontSize: '1.5rem',
          fontWeight: '600'
        }}>
          Choose Your Solana Wallet
        </h3>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {availableSolanaWallets.map((wallet) => (
            <button
              key={wallet.type}
              onClick={() => handleSolanaVerification(wallet.type)}
              disabled={solanaVerifying}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '1rem 1.5rem',
                background: 'rgba(124, 77, 255, 0.1)',
                border: '1px solid rgba(124, 77, 255, 0.3)',
                borderRadius: '12px',
                color: 'white',
                fontSize: '1rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                width: '100%'
              }}
              onMouseOver={(e) => {
                e.target.style.background = 'rgba(124, 77, 255, 0.2)';
                e.target.style.borderColor = 'rgba(124, 77, 255, 0.5)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'rgba(124, 77, 255, 0.1)';
                e.target.style.borderColor = 'rgba(124, 77, 255, 0.3)';
              }}
            >
              <span style={{ fontSize: '1.5rem' }}>{wallet.icon}</span>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontWeight: '600' }}>{wallet.name}</div>
                <div style={{ 
                  fontSize: '0.85rem', 
                  color: 'rgba(255, 255, 255, 0.7)' 
                }}>
                  {wallet.type === 'phantom' ? 'The most popular Solana wallet' : 'Web & mobile Solana wallet'}
                </div>
              </div>
            </button>
          ))}
        </div>
        
        <button
          onClick={() => {
            setShowSolanaWalletSelector(false);
            setSolanaVerifying(false);
          }}
          style={{
            marginTop: '1.5rem',
            padding: '0.75rem 1.5rem',
            background: 'transparent',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '8px',
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '0.9rem',
            cursor: 'pointer',
            width: '100%',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => {
            e.target.style.borderColor = 'rgba(255, 255, 255, 0.4)';
            e.target.style.color = 'white';
          }}
          onMouseOut={(e) => {
            e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            e.target.style.color = 'rgba(255, 255, 255, 0.7)';
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};


  // Add Solana platform button renderer
const renderSolanaPlatformButton = () => {
  const isSolanaVerified = passport?.verifications && passport.verifications['solana'];
  
  if (isSolanaVerified) {
    return (
      <button
        disabled={true}
        className="btn btn-verified"
      >
        <span className="verification-icon"></span> Verified
      </button>
    );
  }
  
  return (
    <button
      onClick={() => handleSolanaVerification()}
      disabled={solanaVerifying || !isConnected}
      className={`platform-status-button ${solanaVerifying ? 'loading' : ''}`}
    >
      {solanaVerifying ? (
        <>
          <span className="loader-sm"></span>
          <span>Verifying...</span>
        </>
      ) : (
        'Verify Solana'
      )}
    </button>
  );
};

  useEffect(() => {
    async function fetchPassport() {
      if (isConnected && account) {
        setLoading(true);
        setError(null);
        try {
          const { passport } = await ApiService.getPassportByAddress(account);
          setPassport(passport);
          
          if (passport && passport.verifications) {
            const verified = {};
            Object.keys(passport.verifications).forEach(platform => {
              verified[platform] = true;
            });
            setVerifiedPlatforms(verified);
          }
        } catch (error) {
          console.error('Error fetching passport:', error);
          setError('Failed to load passport data. Please try again.');
        } finally {
          setLoading(false);
        }
      }
    }

    fetchPassport();
  }, [account, isConnected]);

  useEffect(() => {
    async function fetchCategories() {
      if (isConnected) {
        try {
          setLoadingCategories(true);
          
          const categories = await ApiService.getSupportedCategories();
          
          if (categories && categories.length > 0) {
            setSupportedCategories(categories);

            const savedCategory = localStorage.getItem('selectedPassportCategory');
            if (savedCategory && categories.includes(savedCategory)) {
              setSelectedCategory(savedCategory);
            } else {
              setSelectedCategory(categories[0]);
            }
          }
        } catch (error) {
          console.error('Error fetching categories from API:', error);
        } finally {
          setLoadingCategories(false);
        }
      }
    }

    fetchCategories();
  }, [isConnected]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    localStorage.setItem('selectedPassportCategory', category);
  };

  useEffect(() => {
    async function checkPendingVerifications() {
      if (isConnected && account) {
        try {
          const twitterStatus = await ApiService.getTwitterVerificationStatus();
          if (twitterStatus.verified && !verifiedPlatforms['twitter']) {
            setPendingVerifications(prev => ({
              ...prev,
              twitter: true
            }));
          }
        } catch (error) {
          console.error('Error checking verification status:', error);
        }
      }
    }

    checkPendingVerifications();
  }, [isConnected, account, verifiedPlatforms]);

const createNewPassport = async () => {
  if (!isConnected || !account || !selectedCategory) return;
  
  // First, validate referral code BEFORE setting any loading states or asking for signatures
  if (referralCode && referralCode.trim().length > 0) {
    setValidatingReferral(true);
    
    try {
      const validation = await validateReferralCode(referralCode.trim());
      
      if (!validation.isValid) {
        // Invalid referral code - stop here before any signatures
        notify.error('Invalid referral code. Please check the code and try again.');
        setValidatingReferral(false);
        return;
      }
      
    } catch (validationError) {
      console.error('Error validating referral code:', validationError);
      notify.error('Failed to validate referral code. Please try again.');
      setValidatingReferral(false);
      return;
    }
    
    setValidatingReferral(false);
  }
  
  // Only after validation (if needed) do we proceed with the signature flow
  setCreatingPassport(true);
  setError(null);
  setErrorDetails(null);
  
  try {
    // Now proceed with passport creation (referral code already validated)
    const result = await createPassportWithSignature(selectedCategory, referralCode);
    
    if (result.success) {
      notify.success('🎉 Passport Created!', {
        duration: 3000
      });
      
      const { passport: newPassport } = await ApiService.getPassportByAddress(account);
      setPassport(newPassport);
      
      // Clear referral code after successful creation
      clearReferralCode();
    } else {
      let errorCategory = result.errorCategory;
      
      if (!errorCategory && result.error) {
        const errorMessage = result.error.toLowerCase();
        
        if (errorMessage.includes('rate limit') || errorMessage.includes('creation limit') || errorMessage.includes('too many')) {
          errorCategory = 'rate_limit';
        } else if (errorMessage.includes('already claimed') || errorMessage.includes('account claimed')) {
          errorCategory = 'account_claimed';
        } else if (errorMessage.includes('duplicate') || errorMessage.includes('already exists')) {
          errorCategory = 'duplicate_passport';
        } else if (errorMessage.includes('unauthorized') || errorMessage.includes('not authorized')) {
          errorCategory = 'unauthorized';
        } else if (errorMessage.includes('network') || errorMessage.includes('connection')) {
          errorCategory = 'network';
        }
      }
      
      notify.errorWithSuggestions(
        result.error || 'Failed to create passport',
        errorCategory,
        result.suggestions || []
      );
    }
  } catch (err) {
    console.error('Error creating passport:', err);
    
    // Check if it's a referral validation error
    if (err.message && err.message.includes('validate referral code')) {
      notify.error(err.message);
      setCreatingPassport(false);
      return;
    }
    
    let errorCategory = null;
    const errorMessage = err.message ? err.message.toLowerCase() : '';
    
    if (errorMessage.includes('rate limit') || errorMessage.includes('creation limit')) {
      errorCategory = 'rate_limit';
    } else if (errorMessage.includes('network') || errorMessage.includes('connection')) {
      errorCategory = 'network';
    }
    
    if (errorCategory) {
      notify.errorWithSuggestions(
        err.message || 'Failed to create passport. Please try again.',
        errorCategory,
        []
      );
    } else {
      notify.error('Failed to create passport. Please try again.');
    }
  } finally {
    setCreatingPassport(false);
  }
};

  const revokeSocialAccount = async (platform) => {
    if (!isConnected || !contract || !passport) {
      return;
    }

    setRevoking(true);
    setRevokingPlatform(platform);
    setError(null);

    try {
      const tx = await contract.revokeSocialAccount(passport.id, platform);
      await tx.wait();

      const { passport: updatedPassport } = await ApiService.getPassportByAddress(account);
      setPassport(updatedPassport);
      
      if (updatedPassport && updatedPassport.verifications) {
        const verified = {};
        Object.keys(updatedPassport.verifications).forEach(platform => {
          verified[platform] = true;
        });
        setVerifiedPlatforms(verified);
      }
    } catch (err) {
      console.error('Error revoking social account:', err);
    } finally {
      setRevoking(false);
      setRevokingPlatform(null);
    }
  };

  const formatPlatformName = (platform) => {
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  };

  const isPlatformVerified = (platform) => {
    return verifiedPlatforms[platform] === true;
  };

  const isPendingVerification = (platform) => {
    return pendingVerifications[platform] === true;
  };

  const renderPlatformButton = (platform, displayName, enabled = true) => {
    const isVerified = isPlatformVerified(platform);
    const isPending = isPendingVerification(platform);
    
    if (isVerified) {
      return (
        <button
          onClick={() => revokeSocialAccount(platform)}
          disabled='true'
          className={`btn btn-verified ${revoking && revokingPlatform === platform ? 'loading' : ''}`}
        >
          {revoking && revokingPlatform === platform ? (
            <>
              <span className="loader-sm"></span>
              <span>Revoking...</span>
            </>
          ) : (
            <>
              <span className="verification-icon"></span> Verified
            </>
          )}
        </button>
      );
    }
    
    if (isPending) {
      return (
        <Link
          to={`/verification/${platform}/success`}
          className="btn btn-pending"
        >
          Complete
        </Link>
      );
    }
    
    if (!enabled) {
      return (
        <button
          disabled
          className="btn btn-disabled"
        >
          Coming Soon
        </button>
      );
    }
    
    return (
      <Link
        to={`/verification/${platform}`}
        className={'platform-status-button'}
      >
        Verify {displayName}
      </Link>
    );
  };

  if (!isConnected) {
    return (
      <div className="dashboard-container">
        <div className="hero-section">
          <h1 className="gradient-title">Social Identity Passport</h1>
          <p className="subtitle">
            Verify ownership of your social accounts on the blockchain
          </p>
        </div>
        <center>
          <MobileWalletGuide 
            onConnect={connectWallet}
            isConnecting={isConnecting}
            error={error}
          />
        </center>
        <SolanaWalletSelector />
      </div>
    );
  }
    
  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="glass-card loading-card">
          <div className="loader"></div>
          <p className="loading-text">Loading your passport data...</p>
        </div>
      </div>
    );
  }
  
  if (!passport) {
    return (
      <div className="dashboard-container">
        <div className="hero-section">
          <h1 className="gradient-title">Your Digital Identity</h1>
          <p className="subtitle">Create your on-chain social identity passport</p>
        </div>
        
        <div className="glass-card" style={{
          background: 'rgba(30, 30, 30, 0.7)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          borderRadius: '20px',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 0, 0, 0.2)',
          padding: '2.5rem',
          position: 'relative',
          overflow: 'hidden',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          <h2 className="card-title" style={{
            fontSize: '1.75rem',
            fontWeight: '700',
            marginBottom: '1.5rem',
            textAlign: 'center',
            background: 'linear-gradient(90deg, #fff, rgba(255, 255, 255, 0.7))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>Create Your Social Passport</h2>
          <p className="card-text" style={{
            color: 'rgba(255, 255, 255, 0.7)',
            marginBottom: '2rem',
            fontSize: '1.05rem',
            lineHeight: '1.6',
            textAlign: 'center'
          }}>
            You need to create a Social Passport to verify ownership of your accounts.
          </p>
          
          <div style={{ 
            marginBottom: '2rem',
            backgroundColor: 'rgba(18, 18, 18, 0.6)',
            borderRadius: '16px',
            padding: '1.75rem',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            backdropFilter: 'blur(12px)'
          }}>
            <h3 style={{ 
              fontSize: '1.25rem', 
              marginBottom: '1.25rem', 
              color: 'rgba(255, 255, 255, 0.95)',
              fontWeight: '600',
              letterSpacing: '0.02em',
              textAlign: 'center'
            }}>Select Your Passport Category</h3>
            
            {loadingCategories ? (
              <div style={{ textAlign: 'center', padding: '1rem' }}>
                <div className="loader-sm" style={{ margin: '0 auto 0.5rem' }}></div>
                <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Loading categories...</p>
              </div>
            ) : (
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '1rem', 
                justifyContent: 'center',
                marginBottom: '0.5rem'
              }}>
                {supportedCategories.map((category) => (
                  <div 
                    key={category}
                    onClick={() => handleCategoryChange(category)} 
                    style={{
                      padding: '0.85rem 1.5rem',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      textTransform: 'capitalize',
                      fontSize: '0.95rem',
                      fontWeight: '500',
                      transition: 'all 0.2s ease',
                      backgroundColor: selectedCategory === category 
                        ? 'rgba(124, 77, 255, 0.25)' 
                        : 'rgba(30, 30, 30, 0.6)',
                      border: `1px solid ${selectedCategory === category 
                        ? 'rgba(124, 77, 255, 0.5)' 
                        : 'rgba(255, 255, 255, 0.08)'}`,
                      color: selectedCategory === category 
                        ? 'var(--color-primary)' 
                        : 'rgba(255, 255, 255, 0.75)',
                      boxShadow: selectedCategory === category 
                        ? '0 4px 20px rgba(124, 77, 255, 0.15)' 
                        : '0 4px 12px rgba(0, 0, 0, 0.1)',
                      transform: selectedCategory === category ? 'translateY(-2px)' : 'none',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    {selectedCategory === category && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(120deg, rgba(124, 77, 255, 0.05), rgba(124, 77, 255, 0.2))',
                        zIndex: 0
                      }}></div>
                    )}
                    <span style={{ position: 'relative', zIndex: 1 }}>{category}</span>
                  </div>
                ))}
              </div>
            )}
            <p style={{ 
              marginTop: '1.25rem', 
              fontSize: '0.85rem', 
              color: 'rgba(255, 255, 255, 0.6)', 
              textAlign: 'center',
              fontStyle: 'italic',
              letterSpacing: '0.01em',
              lineHeight: '1.5'
            }}>
              Your category helps connect you with relevant opportunities and communities.
            </p>
          </div>

          <div style={{ 
            marginBottom: '2rem',
            backgroundColor: 'rgba(18, 18, 18, 0.6)',
            borderRadius: '16px',
            padding: '1.75rem',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            backdropFilter: 'blur(12px)'
          }}>
            <h3 style={{ 
              fontSize: '1.25rem', 
              marginBottom: '1.25rem', 
              color: 'rgba(255, 255, 255, 0.95)',
              fontWeight: '600',
              letterSpacing: '0.02em',
              textAlign: 'center'
            }}>
              Referral Code
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {isFromURL && (
                <div style={{
                  padding: '0.75rem 1rem',
                  backgroundColor: 'rgba(124, 77, 255, 0.1)',
                  border: '1px solid rgba(124, 77, 255, 0.3)',
                  borderRadius: '8px',
                  textAlign: 'center',
                  fontSize: '0.85rem',
                  color: 'var(--color-primary)'
                }}>
                  🎉 Referral code automatically applied from your invite link!
                </div>
              )}
              
              <input
                type="text"
                value={referralCode}
                onChange={(e) => updateReferralCode(e.target.value, true)}
                placeholder="Enter referral code"
                disabled={isFromURL && referralCode} // Disable if auto-filled, but allow clearing
                style={{
                  padding: '0.85rem 1.25rem',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  backgroundColor: isFromURL && referralCode 
                    ? 'rgba(124, 77, 255, 0.1)' 
                    : 'rgba(30, 30, 30, 0.8)',
                  color: 'white',
                  fontSize: '0.95rem',
                  fontWeight: '500',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  backdropFilter: 'blur(8px)',
                  textAlign: 'center',
                  letterSpacing: '0.5px'
                }}
                onFocus={(e) => {
                  if (!isFromURL || !referralCode) {
                    e.target.style.borderColor = 'rgba(124, 77, 255, 0.5)';
                    e.target.style.boxShadow = '0 0 0 2px rgba(124, 77, 255, 0.1)';
                  }
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.boxShadow = 'none';
                }}
              />
              
              {isFromURL && referralCode && (
                <button
                  onClick={clearReferralCode}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: 'transparent',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '6px',
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.borderColor = 'rgba(239, 68, 68, 0.4)';
                    e.target.style.color = 'rgba(239, 68, 68, 0.8)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                    e.target.style.color = 'rgba(255, 255, 255, 0.7)';
                  }}
                >
                  Clear referral code
                </button>
              )}
              
              <p style={{ 
                fontSize: '0.8rem', 
                color: 'rgba(255, 255, 255, 0.5)', 
                textAlign: 'center',
                fontStyle: 'italic',
                lineHeight: '1.4'
              }}>
                {isFromURL 
                  ? 'This referral code was automatically detected from your invite link!' 
                  : 'Have a referral code? Enter it above to earn bonus rewards for you and your referrer!'
                }
              </p>
            </div>
          </div>
          
{creatingPassport || validatingReferral ? (
  <div className="creating-passport-state" style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1.5rem',
    textAlign: 'center'
  }}>
    <div className="loader"></div>
    <p className="card-text" style={{
      marginTop: '1rem',
      marginBottom: '0.5rem',
      color: 'rgba(255, 255, 255, 0.7)'
    }}>
      {validatingReferral ? 'Validating referral code...' : 'Creating your passport...'}
    </p>
    <p className="card-subtext" style={{
      color: 'rgba(255, 255, 255, 0.5)',
      fontSize: '0.875rem'
    }}>
      {validatingReferral 
        ? 'Checking referral code validity on the blockchain.'
        : 'This may take a few moments as we process your request on the blockchain.'
      }
    </p>
  </div>
) : (
  <div style={{ textAlign: 'center' }}>
    <button
      onClick={createNewPassport}
      className="btn btn-gradient"
      disabled={
        !selectedCategory || 
        supportedCategories.length === 0 || 
        creatingPassport || 
        validatingReferral
      }
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem 2rem',
        fontSize: '1.1rem',
        fontWeight: '600',
        borderRadius: '5px',
        border: 'none',
        cursor: (!selectedCategory || supportedCategories.length === 0 || creatingPassport || validatingReferral) ? 'not-allowed' : 'pointer',
        transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        position: 'relative',
        overflow: 'hidden',
        background: (!selectedCategory || supportedCategories.length === 0 || creatingPassport || validatingReferral)
          ? 'rgba(124, 77, 255, 0.2)' 
          : 'linear-gradient(135deg, #7c4dff 0%, #00ccb8 100%)',
        color: 'white',
        boxShadow: (!selectedCategory || supportedCategories.length === 0 || creatingPassport || validatingReferral)
          ? 'none'
          : '0 4px 15px rgba(0, 0, 0, 0.2), 0 0 10px rgba(124, 77, 255, 0.3)',
        minWidth: '200px',
        opacity: (!selectedCategory || supportedCategories.length === 0 || creatingPassport || validatingReferral) ? 0.6 : 1
      }}
    >
      <RiPassportLine style={{ fontSize: '20px' }} />
      Create Passport
    </button>
  </div>
)}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="hero-section">
        <h1 className="gradient-title">Your Digital Identity</h1>
        <p className="subtitle">
          Manage and verify your on-chain social identity
        </p>
      </div>
      
      {error && (
        <div className="alert-error">
          <p>{error}</p>
        </div>
      )}
      
      <div className="passport-display-section">
        <PassportCard 
          passport={passport} 
          verifications={passport.verifications || {}} 
        />
      </div>

      {/* Rewards Dashboard Section */}
      {(passport?.totalPoints > 0 || passport?.referralCode || passport?.totalReferrals > 0) && (
        <div className="glass-card rewards-dashboard" style={{ marginBottom: '2rem' }}>
          <h3 className="card-title">🏆 Your Points</h3>
          <p className="card-text" style={{ marginBottom: '1.5rem' }}>
            Track your points, referrals, and share your code to earn more points.
          </p>
          
          <div className="rewards-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1.5rem',
            marginBottom: '1.5rem'
          }}>
            {/* Points Card */}
            <div className="reward-stat-card" style={{
              background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 215, 0, 0.05) 100%)',
              border: '1px solid rgba(255, 215, 0, 0.2)',
              borderRadius: '12px',
              padding: '1.5rem',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '2rem',
                color: '#FFD700',
                marginBottom: '0.5rem'
              }}>
                <FaTrophy />
              </div>
              <div style={{
                fontSize: '1.75rem',
                fontWeight: '700',
                color: '#FFD700',
                marginBottom: '0.25rem'
              }}>
                {passport.totalPoints || 0}
              </div>
              <div style={{
                fontSize: '0.9rem',
                color: 'rgba(255, 255, 255, 0.8)',
                fontWeight: '500'
              }}>
                Total Points
              </div>
            </div>

            {/* Referrals Card */}
            <div className="reward-stat-card" style={{
              background: 'linear-gradient(135deg, rgba(0, 204, 184, 0.1) 0%, rgba(0, 204, 184, 0.05) 100%)',
              border: '1px solid rgba(0, 204, 184, 0.2)',
              borderRadius: '12px',
              padding: '1.5rem',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '2rem',
                color: '#00ccb8',
                marginBottom: '0.5rem'
              }}>
                <FaUsers />
              </div>
              <div style={{
                fontSize: '1.75rem',
                fontWeight: '700',
                color: '#00ccb8',
                marginBottom: '0.25rem'
              }}>
                {passport.totalReferrals || 0}
              </div>
              <div style={{
                fontSize: '0.9rem',
                color: 'rgba(255, 255, 255, 0.8)',
                fontWeight: '500'
              }}>
                Successful Referrals
              </div>
            </div>

            {/* Referral Code Card */}
            {passport?.referralCode && (
              <div className="reward-stat-card" style={{
                background: 'linear-gradient(135deg, rgba(124, 77, 255, 0.1) 0%, rgba(124, 77, 255, 0.05) 100%)',
                border: '1px solid rgba(124, 77, 255, 0.2)',
                borderRadius: '12px',
                padding: '1.5rem',
                textAlign: 'center',
                gridColumn: passport.totalReferrals === 0 ? 'span 2' : 'auto'
              }}>
                <div style={{
                  fontSize: '2rem',
                  color: 'var(--color-primary)',
                  marginBottom: '0.5rem'
                }}>
                  <FaCode />
                </div>
                <div style={{
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  color: 'var(--color-primary)',
                  marginBottom: '0.5rem',
                  fontFamily: 'monospace',
                  letterSpacing: '2px'
                }}>
                  {passport.referralCode}
                </div>
                <div style={{
                  fontSize: '0.9rem',
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontWeight: '500',
                  marginBottom: '1rem'
                }}>
                  Your Referral Code
                </div>
                <button
                  onClick={() => {
                    const referralURL = generateReferralURL(passport.referralCode);
                    navigator.clipboard.writeText(referralURL);
                    notify.copied('Referral link copied! 🎉');
                  }}
                  style={{
                    background: 'rgba(124, 77, 255, 0.2)',
                    border: '1px solid rgba(124, 77, 255, 0.4)',
                    borderRadius: '6px',
                    padding: '0.5rem 1rem',
                    color: 'var(--color-primary)',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = 'rgba(124, 77, 255, 0.3)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = 'rgba(124, 77, 255, 0.2)';
                  }}
                >
                  Copy Link
                </button>
              </div>
            )}
          </div>

          <div style={{
            background: 'linear-gradient(135deg, rgba(124, 77, 255, 0.08) 0%, rgba(0, 204, 184, 0.05) 100%)',
            border: '1px solid rgba(124, 77, 255, 0.2)',
            borderRadius: '12px',
            padding: '1.5rem',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(45deg, rgba(124, 77, 255, 0.03) 0%, rgba(0, 204, 184, 0.03) 100%)',
              zIndex: 0
            }}></div>
            
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1.25rem'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, var(--color-primary) 0%, #00ccb8 100%)',
                  borderRadius: '50%',
                  width: '2.5rem',
                  height: '2.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.2rem',
                  boxShadow: '0 4px 15px rgba(124, 77, 255, 0.3)'
                }}>
                  💡
                </div>
                <h4 style={{
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  margin: 0,
                  background: 'linear-gradient(90deg, var(--color-primary), #00ccb8)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  How to Earn More Points
                </h4>
              </div>
              
              <div style={{
                display: 'grid',
                gap: '1rem',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  padding: '0.75rem',
                  background: 'rgba(124, 77, 255, 0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(124, 77, 255, 0.1)'
                }}>
                  <div style={{
                    background: 'rgba(124, 77, 255, 0.2)',
                    borderRadius: '50%',
                    width: '1.75rem',
                    height: '1.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.8rem',
                    flexShrink: 0
                  }}>
                    ✅
                  </div>
                  <div>
                    <div style={{
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: 'rgba(255, 255, 255, 0.9)',
                      marginBottom: '0.25rem'
                    }}>
                      Verify Social Accounts
                    </div>
                    <div style={{
                      fontSize: '0.8rem',
                      color: 'rgba(255, 255, 255, 0.7)',
                      lineHeight: '1.4'
                    }}>
                      Connect Twitter, GitHub & more to earn points
                    </div>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  padding: '0.75rem',
                  background: 'rgba(0, 204, 184, 0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(0, 204, 184, 0.1)'
                }}>
                  <div style={{
                    background: 'rgba(0, 204, 184, 0.2)',
                    borderRadius: '50%',
                    width: '1.75rem',
                    height: '1.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.8rem',
                    flexShrink: 0
                  }}>
                    🔗
                  </div>
                  <div>
                    <div style={{
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: 'rgba(255, 255, 255, 0.9)',
                      marginBottom: '0.25rem'
                    }}>
                      Share Your Code
                    </div>
                    <div style={{
                      fontSize: '0.8rem',
                      color: 'rgba(255, 255, 255, 0.7)',
                      lineHeight: '1.4'
                    }}>
                      Invite friends with your referral code
                    </div>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  padding: '0.75rem',
                  background: 'rgba(255, 215, 0, 0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 215, 0, 0.1)'
                }}>
                  <div style={{
                    background: 'rgba(255, 215, 0, 0.2)',
                    borderRadius: '50%',
                    width: '1.75rem',
                    height: '1.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.8rem',
                    flexShrink: 0
                  }}>
                    🎯
                  </div>
                  <div>
                    <div style={{
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: 'rgba(255, 255, 255, 0.9)',
                      marginBottom: '0.25rem'
                    }}>
                      Early Supporter Rewards
                    </div>
                    <div style={{
                      fontSize: '0.8rem',
                      color: 'rgba(255, 255, 255, 0.7)',
                      lineHeight: '1.4'
                    }}>
                      Early adopters get exclusive bonus points and perks
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
  
      <div className="glass-card verification-options" style={{ marginBottom: '3rem' }}>
        <h3 className="card-title">Verified Accounts</h3>
        
        {!passport.verifications || Object.keys(passport.verifications).length === 0 ? (
          <div className="empty-state">
            <p className="card-text">
              You don't have any verified accounts yet.
            </p>
          </div>
        ) : (
<div className="verifications-list">
  {Object.entries(passport.verifications).map(([platform, data]) => (
    <div key={platform} className="verification-item">
      <div className="verification-header">
        <div className={`platform-badge ${platform}`}>
          <span className={`platform-dot ${platform}-dot`}></span>
          {formatPlatformName(platform)}
        </div>
        <span className="verification-id">
          {platform === 'solana' 
            ? `${data.identifier.substring(0, 8)}...${data.identifier.substring(data.identifier.length - 8)}`
            : data.identifier
          }
        </span>
        <button
          onClick={() => revokeSocialAccount(platform)}
          disabled={revoking && revokingPlatform === platform}
          className={`btn btn-outline danger ${
            revoking && revokingPlatform === platform ? 'loading' : ''
          }`}
        >
          {revoking && revokingPlatform === platform ? (
            <>
              <span className="loader-sm"></span>
              <span>Revoking...</span>
            </>
          ) : (
            'Revoke'
          )}
        </button>
      </div>
    </div>
  ))}
</div>
        )}
      </div>
      
      <div className="glass-card" style={{ marginTop: '3rem' }}>
        <h3 className="card-title">Add Verification</h3>
        <p className="card-text">
          Verify ownership of additional social platforms to enhance your digital identity.
        </p>
        
        <div className="platform-grid">
          <div className="platform-item">
            <div className="platform-icon-container">
              <FaTwitter className="platform-icon-svg twitter" />
            </div>
            <h4 className="platform-name">Twitter</h4>
            {renderPlatformButton('twitter', 'Twitter')}
          </div>

          <div className="platform-item">
            <div className="platform-icon-container">
              <FaGithub className="platform-icon-svg github" />
            </div>
            <h4 className="platform-name">GitHub</h4>
            {renderPlatformButton('github', 'GitHub', true)}
          </div>

          <div className="platform-item">
            <div className="platform-icon-container">
              <FaWallet className="platform-icon-svg solana" />
            </div>
            <h4 className="platform-name">Solana</h4>
            {renderSolanaPlatformButton()}
          </div>
          
          <div className="platform-item">
            <div className="platform-icon-container">
              <FaDiscord className="platform-icon-svg discord" />
            </div>
            <h4 className="platform-name">Discord</h4>
            {renderPlatformButton('discord', 'Discord', false)}
          </div>
        </div>
      </div>
      
      <SolanaWalletSelector />
    </div>
  );
}

export default Dashboard;